import _ from 'lodash';
import {
  CATEGORY_SHEET_NAME,
  IMPORTABLE_EXCEL_FORMAT_LIST,
  ITEM_SHEET_NAME,
  NG_SAMPLE_SHEET_NAME,
  TYPE_SHEET_NAME,
} from '../../../_constants/manageConst';
import {
  PutItemMaster,
  PostItemMaster,
  Attribute,
  PostCategoryMaster,
  PutCategoryMaster,
  PostTypeMaster,
  PutTypeMaster,
} from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  postItemMaster,
  putItemMaster,
  deleteItemMaster,
} from '../../../services/api/itemMaster';
import { RootState } from '../../../stores/store';
import {
  ImportCategoryForExcel,
  ImportNgSampleForExcel,
  ImportItemForExcel,
  ImportTypeForExcel,
} from '../../../types/ExcelType';
import XLSX from 'xlsx';
import {
  adjustToMaxDigits,
  convertInputDataToCorrectType,
  isEmptyData,
  isNullOrUndefined,
} from '../../../_utils/utils';
import { VALID_NUMBER_DIGITS } from '../../../_constants/Const';
import { API_CODE } from '../../../_constants/apiConst';
import { Options } from '../ManageInspectionItem';
import {
  deleteCategoryMaster,
  postCategoryMaster,
  putCategoryMaster,
} from '../../../services/api/categoryMaster';
import {
  deleteTypeMaster,
  postTypeMaster,
  putTypeMaster,
} from '../../../services/api/typeMaster';

type CategoryMasterDiff = {
  post: PostCategoryMaster[];
  put: PutCategoryMaster[];
  delete: string[]; // 大項目IDのみ
};

type TypeMasterDiff = {
  post: PostTypeMaster[];
  put: PutTypeMaster[];
  delete: string[]; // 中項目IDのみ
};

type ItemMasterDiff = {
  post: PostItemMaster[];
  put: PutItemMaster[];
  delete: DeleteItemMaster[];
};

type DeleteItemMaster = {
  itemId: string; // リクエストパラメータ用
  depth: number; // リクエストの順序整理用
};

type ImportErrorItem = {
  sheet: string;
  row: number;
  errorItem: string;
  errorType: string;
};

type useImportProps = {
  itemOptions: Options;
  importFile: File | undefined;
  setImportFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  setIsOpenImportModal: React.Dispatch<React.SetStateAction<boolean>>;
  setImportStatus: React.Dispatch<React.SetStateAction<0 | 1 | 2 | 3 | 4>>;
  setImportErrorMsg: React.Dispatch<React.SetStateAction<string>>;
  getApiErrorMessage: (apiError: unknown, apiCode: string) => string;
};

const useImport = ({
  itemOptions,
  importFile,
  setImportFile,
  setIsOpenImportModal,
  setImportErrorMsg,
  setImportStatus,
  getApiErrorMessage,
}: useImportProps) => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);

  /** メソッドに入力される種類 */
  const METHOD = {
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
  };

  // undefinedの項目をリスト化
  const undefinedMemberList = (obj: any) => {
    const result: string[] = [];
    for (const key in obj) {
      if (obj[key] === undefined) {
        result.push(key);
      }
    }
    return result;
  };

  const createAttributeList = (
    attributesString: string | null,
    attributeMasterList: Attribute[],
  ) => {
    const attributeList: Attribute[] = [];
    if (attributesString === undefined || attributesString === null) {
      return attributeList;
    }

    attributesString.split(',').forEach((attributeName) => {
      const target = attributeMasterList.find(
        (attr) => attr.name === attributeName,
      );
      if (target) {
        attributeList.push({ id: target.id, name: attributeName });
      }
    });
    return attributeList;
  };

  /** displayNameにセットする言語の項目を取得する */
  const getItemName = (item: ImportItemForExcel) => {
    switch (userData.language) {
      case 'en':
        return item.itemName;
      case 'ja':
        return item.itemNameJp;
      case 'lang3':
        return item.itemName03;
      case 'lang4':
        return item.itemName04;
      case 'lang5':
        return item.itemName05;
      case 'lang6':
        return item.itemName06;
      case 'lang7':
        return item.itemName07;
      case 'lang8':
        return item.itemName08;
      case 'lang9':
        return item.itemName09;
      case 'lang10':
        return item.itemName10;
      default:
        return '';
    }
  };

  /** インポートキャンセル */
  const onClickImportCancel = () => {
    setIsOpenImportModal(false);
    setImportStatus(0);
    setImportFile(undefined);
    setImportErrorMsg('');
  };

  /** インポートOK */
  const onClickImportOk = () => {
    setImportErrorMsg('');
    doImportInspection(importFile);
  };

  // インポート実行
  const doImportInspection = (file: File | undefined) => {
    setImportStatus(1);
    // ファイルが未選択
    if (!file) {
      setImportStatus(3);
      setImportErrorMsg(t('ERR_IMPORT'));
      return;
    }

    // インポートできないフォーマットのファイル
    if (!IMPORTABLE_EXCEL_FORMAT_LIST.includes(file.type)) {
      setImportStatus(3);
      setImportErrorMsg(t('ERR_IMPORT'));
      return;
    }

    file.arrayBuffer().then((buffer) => {
      const workbook = XLSX.read(buffer, { type: 'buffer', bookVBA: true });
      // Excelの各シート
      const categorySheet = workbook.Sheets[CATEGORY_SHEET_NAME];
      const typeSheet = workbook.Sheets[TYPE_SHEET_NAME];
      const ngSampleSheet = workbook.Sheets[NG_SAMPLE_SHEET_NAME];
      const itemSheet = workbook.Sheets[ITEM_SHEET_NAME];

      // シートが無ければエラーにする
      if (
        categorySheet === undefined ||
        typeSheet === undefined ||
        ngSampleSheet === undefined ||
        itemSheet === undefined
      ) {
        setImportStatus(3);
        setImportErrorMsg(t('ERR_IMPORT'));
        return;
      }
      // シート内のデータを抽出
      const categoryData = XLSX.utils.sheet_to_json(categorySheet, {
        raw: true,
        blankrows: true,
      });
      const typeData = XLSX.utils.sheet_to_json(typeSheet, {
        raw: true,
        blankrows: true,
      });
      const ngSampleData = XLSX.utils.sheet_to_json(ngSampleSheet, {
        raw: true,
        blankrows: true,
      });
      const itemData = XLSX.utils.sheet_to_json(itemSheet, {
        raw: true,
        blankrows: true,
      });

      try {
        // 入力チェックを行い、更新する項目のリストを作成する
        const errorList: ImportErrorItem[] = [];
        const categoryMaster = createFormattedCategoryList(
          categoryData,
          errorList,
        );
        const typeMaster = createFormattedTypeList(typeData, errorList);
        const ngSamples = createFormattedNgSampleList(ngSampleData, errorList);
        const itemMaster = createFormattedItemList(itemData, errorList);

        // エラーがあれば処理終了
        if (errorList.length !== 0) {
          throw new Error('input data error');
        }

        // 指定されたmethodごとに振り分ける
        const categoryMasterDiff: CategoryMasterDiff =
          getCategoryMasterMethod(categoryMaster);

        const typeMasterDiff: TypeMasterDiff = getTypeMasterMethod(
          typeMaster,
          ngSamples,
        );

        const itemMasterDiff: ItemMasterDiff = getItemMasterMethod(itemMaster);

        if (
          categoryMasterDiff.post.length === 0 &&
          categoryMasterDiff.put.length === 0 &&
          categoryMasterDiff.delete.length === 0 &&
          typeMasterDiff.post.length === 0 &&
          typeMasterDiff.put.length === 0 &&
          typeMasterDiff.delete.length === 0 &&
          itemMasterDiff.post.length === 0 &&
          itemMasterDiff.put.length === 0 &&
          itemMasterDiff.delete.length === 0
        ) {
          setImportStatus(4);
          return;
        }

        doPostMaster(categoryMasterDiff, typeMasterDiff, itemMasterDiff)
          .then(() => {
            doPutMaster(categoryMasterDiff, typeMasterDiff, itemMasterDiff)
              .then(() => {
                doDeleteMaster(
                  categoryMasterDiff,
                  typeMasterDiff,
                  itemMasterDiff,
                )
                  .then(() => {
                    setImportStatus(2);
                    return;
                  })
                  .catch(() => {
                    setImportStatus(3);
                    return;
                  });
              })
              .catch(() => {
                setImportStatus(3);
                return;
              });
          })
          .catch(() => {
            setImportStatus(3);
            return;
          });
      } catch {
        setImportStatus(3);
        setImportErrorMsg(t('ERR_IMPORT'));
        return;
      }
    });
  };

  /** インポートした大分類データをインポート用データに変換する */
  const createFormattedCategoryList = (
    categoryData: unknown[],
    errorList: ImportErrorItem[],
  ) => {
    const categoryMaster: ImportCategoryForExcel[] = JSON.parse(
      JSON.stringify(categoryData),
    );

    categoryMaster.forEach((data, i) => {
      data.categoryId = convertInputDataToCorrectType(
        data.categoryId,
        'string',
      );
      data.categoryName = convertInputDataToCorrectType(
        data.categoryName,
        'string',
      );
      data.categoryNameJp = convertInputDataToCorrectType(
        data.categoryNameJp,
        'string',
      );
      data.categoryName03 = convertInputDataToCorrectType(
        data.categoryName03,
        'string',
      );
      data.categoryName04 = convertInputDataToCorrectType(
        data.categoryName04,
        'string',
      );
      data.categoryName05 = convertInputDataToCorrectType(
        data.categoryName05,
        'string',
      );
      data.categoryName06 = convertInputDataToCorrectType(
        data.categoryName06,
        'string',
      );
      data.categoryName07 = convertInputDataToCorrectType(
        data.categoryName07,
        'string',
      );
      data.categoryName08 = convertInputDataToCorrectType(
        data.categoryName08,
        'string',
      );
      data.categoryName09 = convertInputDataToCorrectType(
        data.categoryName09,
        'string',
      );
      data.categoryName10 = convertInputDataToCorrectType(
        data.categoryName10,
        'string',
      );
      data.sort = convertInputDataToCorrectType(data.sort, 'number');
      if (!isNullOrUndefined(data.sort)) {
        adjustToMaxDigits(data.sort, VALID_NUMBER_DIGITS.FOUR);
      }
      data.isOptionalAddition = convertInputDataToCorrectType(
        data.isOptionalAddition,
        'boolean',
      );

      data.method = !isEmptyData(data.method) ? data.method.toUpperCase() : '';

      const list = undefinedMemberList(data);
      list.forEach((value) => {
        const item: ImportErrorItem = {
          sheet: CATEGORY_SHEET_NAME,
          row: i + 2,
          errorItem: value,
          errorType: 'type error',
        };
        errorList.push(item);
      });
    });

    return categoryMaster;
  };

  /** インポートした中分類データをインポート用データに変換する */
  const createFormattedTypeList = (
    typeData: unknown[],
    errorList: ImportErrorItem[],
  ) => {
    const excelTypeData: ImportTypeForExcel[] = JSON.parse(
      JSON.stringify(typeData),
    );
    excelTypeData.forEach((data, i) => {
      data.categoryId = convertInputDataToCorrectType(
        data.categoryId,
        'string',
      );
      data.documentPath = convertInputDataToCorrectType(
        data.documentPath,
        'string',
      );
      data.isVisualInspection = convertInputDataToCorrectType(
        data.isVisualInspection,
        'boolean',
      );
      data.isOptionalAddition = convertInputDataToCorrectType(
        data.isOptionalAddition,
        'boolean',
      );
      data.sort = convertInputDataToCorrectType(data.sort, 'number');
      if (!isNullOrUndefined(data.sort)) {
        adjustToMaxDigits(data.sort, VALID_NUMBER_DIGITS.FOUR);
      }
      data.typeId = convertInputDataToCorrectType(data.typeId, 'string');
      data.typeName = convertInputDataToCorrectType(data.typeName, 'string');
      data.typeName03 = convertInputDataToCorrectType(
        data.typeName03,
        'string',
      );
      data.typeName04 = convertInputDataToCorrectType(
        data.typeName04,
        'string',
      );
      data.typeName05 = convertInputDataToCorrectType(
        data.typeName05,
        'string',
      );
      data.typeName06 = convertInputDataToCorrectType(
        data.typeName06,
        'string',
      );
      data.typeName07 = convertInputDataToCorrectType(
        data.typeName07,
        'string',
      );
      data.typeName08 = convertInputDataToCorrectType(
        data.typeName08,
        'string',
      );
      data.typeName09 = convertInputDataToCorrectType(
        data.typeName09,
        'string',
      );
      data.typeName10 = convertInputDataToCorrectType(
        data.typeName10,
        'string',
      );
      data.typeNameJp = convertInputDataToCorrectType(
        data.typeNameJp,
        'string',
      );

      data.displayType = convertInputDataToCorrectType(
        data.displayType,
        'number',
      );

      data.method = !isEmptyData(data.method) ? data.method.toUpperCase() : '';

      const list = undefinedMemberList(data);
      list.forEach((value) => {
        const item: ImportErrorItem = {
          sheet: TYPE_SHEET_NAME,
          row: i + 2,
          errorItem: value,
          errorType: 'type error',
        };
        errorList.push(item);
      });
    });

    return excelTypeData;
  };

  /** インポートしたNGサンプルデータをインポート用データに変換する */
  const createFormattedNgSampleList = (
    ngSampleData: unknown[],
    errorList: ImportErrorItem[],
  ) => {
    const excelNgSampleData: ImportNgSampleForExcel[] = JSON.parse(
      JSON.stringify(ngSampleData),
    );

    excelNgSampleData.forEach((data, i) => {
      data.imageNo = convertInputDataToCorrectType(data.imageNo, 'number');
      data.imageNo = adjustToMaxDigits(data.imageNo, VALID_NUMBER_DIGITS.FOUR);
      data.imagePath = convertInputDataToCorrectType(data.imagePath, 'string');
      data.labelName = convertInputDataToCorrectType(data.labelName, 'string');
      data.labelName03 = convertInputDataToCorrectType(
        data.labelName03,
        'string',
      );
      data.labelName04 = convertInputDataToCorrectType(
        data.labelName04,
        'string',
      );
      data.labelName05 = convertInputDataToCorrectType(
        data.labelName05,
        'string',
      );
      data.labelName06 = convertInputDataToCorrectType(
        data.labelName06,
        'string',
      );
      data.labelName07 = convertInputDataToCorrectType(
        data.labelName07,
        'string',
      );
      data.labelName08 = convertInputDataToCorrectType(
        data.labelName08,
        'string',
      );
      data.labelName09 = convertInputDataToCorrectType(
        data.labelName09,
        'string',
      );
      data.labelName10 = convertInputDataToCorrectType(
        data.labelName10,
        'string',
      );
      data.labelNameJp = convertInputDataToCorrectType(
        data.labelNameJp,
        'string',
      );
      data.sort = convertInputDataToCorrectType(data.sort, 'number');
      if (!isNullOrUndefined(data.sort)) {
        adjustToMaxDigits(data.sort, VALID_NUMBER_DIGITS.FOUR);
      }
      data.typeId = convertInputDataToCorrectType(data.typeId, 'string');

      data.method = !isEmptyData(data.method) ? data.method.toUpperCase() : '';

      const list = undefinedMemberList(data);
      list.forEach((value) => {
        const item: ImportErrorItem = {
          sheet: NG_SAMPLE_SHEET_NAME,
          row: i + 2,
          errorItem: value,
          errorType: 'type error',
        };
        errorList.push(item);
      });
    });

    return excelNgSampleData;
  };

  /** インポートした小分類データをインポート用データに変換する */
  const createFormattedItemList = (
    itemData: unknown[],
    errorList: ImportErrorItem[],
  ): ImportItemForExcel[] => {
    const formattedItem: ImportItemForExcel[] = JSON.parse(
      JSON.stringify(itemData),
    );
    formattedItem.forEach((data, i) => {
      data.attribute1 = convertInputDataToCorrectType(
        data.attribute1,
        'string',
      );
      data.attribute2 = convertInputDataToCorrectType(
        data.attribute2,
        'string',
      );
      data.attribute3 = convertInputDataToCorrectType(
        data.attribute3,
        'string',
      );
      data.attribute5 = convertInputDataToCorrectType(
        data.attribute5,
        'string',
      );
      data.attribute6 = convertInputDataToCorrectType(
        data.attribute6,
        'string',
      );
      data.brandName = convertInputDataToCorrectType(data.brandName, 'string');
      data.categoryId = convertInputDataToCorrectType(
        data.categoryId,
        'string',
      );
      data.conditions = convertInputDataToCorrectType(
        data.conditions,
        'string',
      );
      data.criteriaMax = convertInputDataToCorrectType(
        data.criteriaMax,
        'number',
      );
      data.criteriaMin = convertInputDataToCorrectType(
        data.criteriaMin,
        'number',
      );
      data.criteriaText = convertInputDataToCorrectType(
        data.criteriaText,
        'string',
      );
      data.depth = convertInputDataToCorrectType(data.depth, 'number');
      data.fCode = convertInputDataToCorrectType(data.fCode, 'string');
      data.isNumberInput = convertInputDataToCorrectType(
        data.isNumberInput,
        'boolean',
      );
      data.isOptionalAddition = convertInputDataToCorrectType(
        data.isOptionalAddition,
        'boolean',
      );
      data.itemId = convertInputDataToCorrectType(data.itemId, 'string');
      data.itemName = convertInputDataToCorrectType(data.itemName, 'string');
      data.itemName03 = convertInputDataToCorrectType(
        data.itemName03,
        'string',
      );
      data.itemName04 = convertInputDataToCorrectType(
        data.itemName04,
        'string',
      );
      data.itemName05 = convertInputDataToCorrectType(
        data.itemName05,
        'string',
      );
      data.itemName06 = convertInputDataToCorrectType(
        data.itemName06,
        'string',
      );
      data.itemName07 = convertInputDataToCorrectType(
        data.itemName07,
        'string',
      );
      data.itemName08 = convertInputDataToCorrectType(
        data.itemName08,
        'string',
      );
      data.itemName09 = convertInputDataToCorrectType(
        data.itemName09,
        'string',
      );
      data.itemName10 = convertInputDataToCorrectType(
        data.itemName10,
        'string',
      );
      data.itemNameJp = convertInputDataToCorrectType(
        data.itemNameJp,
        'string',
      );
      data.pCode = convertInputDataToCorrectType(data.pCode, 'string');
      data.parentItemId = convertInputDataToCorrectType(
        data.parentItemId,
        'string',
      );
      data.rCode = convertInputDataToCorrectType(data.rCode, 'string');
      data.sort = convertInputDataToCorrectType(data.sort, 'number');
      if (!isNullOrUndefined(data.sort)) {
        adjustToMaxDigits(data.sort, VALID_NUMBER_DIGITS.FOUR);
      }
      data.typeId = convertInputDataToCorrectType(data.typeId, 'string');
      data.memo = convertInputDataToCorrectType(data.memo, 'string');

      data.method = !isEmptyData(data.method) ? data.method.toUpperCase() : '';

      const list = undefinedMemberList(data);
      list.forEach((value) => {
        const item: ImportErrorItem = {
          sheet: ITEM_SHEET_NAME,
          row: i + 2,
          errorItem: value,
          errorType: 'type error',
        };
        errorList.push(item);
      });
    });

    return formattedItem;
  };

  /** 大項目をメソッドごとに取得 */
  const getCategoryMasterMethod = (
    updateData: ImportCategoryForExcel[],
  ): CategoryMasterDiff => {
    const result: CategoryMasterDiff = { post: [], put: [], delete: [] };

    // POST
    const postCategoryList = updateData.filter(
      (data) => data.method === METHOD.POST,
    );
    postCategoryList.forEach((category) => {
      const postCategory: PostCategoryMaster = {
        categoryName: category.categoryName,
        isOptionalAddition: category.isOptionalAddition,
      };
      result.post.push(postCategory);
    });

    // DELETE
    result.delete = updateData
      .filter((data) => data.method === METHOD.DELETE)
      .map((c) => c.categoryId);

    // PUT
    const putCategoryList = updateData.filter(
      (data) => data.method === METHOD.PUT,
    );
    putCategoryList.forEach((category) => {
      const { method, displayName, ...categoryData } = category;
      const putCategory: PutCategoryMaster = {
        ...categoryData,
      };
      result.put.push(putCategory);
    });

    return result;
  };

  /** 中項目をメソッドごとに取得 */
  const getTypeMasterMethod = (
    updateData: ImportTypeForExcel[],
    ngSampleData: ImportNgSampleForExcel[],
  ): TypeMasterDiff => {
    const result: TypeMasterDiff = { post: [], put: [], delete: [] };

    // POST
    const postTypeList = updateData.filter(
      (data) => data.method === METHOD.POST,
    );
    postTypeList.forEach((type) => {
      const postType: PostTypeMaster = {
        categoryId: type.categoryId,
        typeName: type.typeName,
        isOptionalAddition: type.isOptionalAddition,
        displayType: type.displayType,
      };
      result.post.push(postType);
    });

    // DELETE
    result.delete = updateData
      .filter((data) => data.method === METHOD.DELETE)
      .map((t) => t.typeId);

    // PUT
    // NGサンプルも一緒にputするため、更新するタイプIDを抽出
    const updateNgList = ngSampleData
      .filter((data) => data.method)
      .map((n) => n.typeId);
    // NGサンプルを更新するIDも更新対象とする
    const putTypeList = updateData.filter(
      (data) =>
        data.method === METHOD.PUT || updateNgList.includes(data.typeId),
    );
    putTypeList.forEach((type) => {
      const ngSample = ngSampleData.filter(
        (ng) => ng.method !== METHOD.DELETE && ng.typeId === type.typeId,
      );
      const { method, displayType, ...typeData } = type;
      const putType: PutTypeMaster = {
        ...typeData,
        typeImageMaster: ngSample,
      };
      result.put.push(putType);
    });

    return result;
  };

  /** 小項目をメソッドごとに取得 */
  const getItemMasterMethod = (
    updateData: ImportItemForExcel[],
  ): ItemMasterDiff => {
    const result: ItemMasterDiff = { post: [], put: [], delete: [] };

    // POST
    const postItemList = updateData.filter(
      (data) => data.method === METHOD.POST,
    );
    postItemList.forEach((item) => {
      const {
        method,
        isNumberInput,
        criteriaText,
        criteriaMax,
        criteriaMin,
        attribute1,
        attribute2,
        attribute3,
        attribute5,
        attribute6,
        brandName,
        conditions,
        rCode,
        ...itemData
      } = item;
      // POST用に整形する
      const postItem: PostItemMaster = {
        ...itemData,
        parentItemId:
          itemData.parentItemId === '' ? null : itemData.parentItemId,
        inputType: isNumberInput ? 1 : 0,
        criteria: {
          text: item.criteriaText,
          max: item.criteriaMax,
          min: item.criteriaMin,
        },
        selectedAttribute1: createAttributeList(
          attribute1,
          itemOptions.attribute1MasterList,
        ),
        selectedAttribute2: createAttributeList(
          attribute2,
          itemOptions.attribute2MasterList,
        ),
        selectedAttribute3: createAttributeList(
          attribute3,
          itemOptions.attribute3MasterList,
        ),
        selectedAttribute5: createAttributeList(
          attribute5,
          itemOptions.attribute5MasterList,
        ),
        selectedAttribute6: createAttributeList(
          attribute6,
          itemOptions.attribute6MasterList,
        ),
        selectedBrandName: createAttributeList(
          brandName,
          itemOptions.brandMasterList,
        ),
        selectedConditions: conditions,
        rCodeList: rCode ? rCode.split(',') : [],
      };
      result.post.push(postItem);
    });

    // DELETE
    result.delete = updateData.filter((data) => data.method === METHOD.DELETE);

    // PUT
    const putItemList = updateData.filter((data) => data.method === METHOD.PUT);
    putItemList.forEach((item) => {
      const {
        method,
        isNumberInput,
        criteriaText,
        criteriaMax,
        criteriaMin,
        attribute1,
        attribute2,
        attribute3,
        attribute5,
        attribute6,
        brandName,
        conditions,
        rCode,
        ...itemData
      } = item;
      // PUT用に整形する
      const putItem: PutItemMaster = {
        ...itemData,
        parentItemId:
          itemData.parentItemId === '' ? null : itemData.parentItemId,
        inputType: isNumberInput ? 1 : 0,
        criteria: {
          text: item.criteriaText,
          max: item.criteriaMax,
          min: item.criteriaMin,
        },
        selectedAttribute1: createAttributeList(
          attribute1,
          itemOptions.attribute1MasterList,
        ),
        selectedAttribute2: createAttributeList(
          attribute2,
          itemOptions.attribute2MasterList,
        ),
        selectedAttribute3: createAttributeList(
          attribute3,
          itemOptions.attribute3MasterList,
        ),
        selectedAttribute5: createAttributeList(
          attribute5,
          itemOptions.attribute5MasterList,
        ),
        selectedAttribute6: createAttributeList(
          attribute6,
          itemOptions.attribute6MasterList,
        ),
        selectedBrandName: createAttributeList(
          brandName,
          itemOptions.brandMasterList,
        ),
        selectedConditions: conditions,
        rCodeList: rCode ? rCode.split(',') : [],
        displayName: getItemName(item),
      };
      result.put.push(putItem);
    });

    return result;
  };

  const doPostMaster = async (
    category: CategoryMasterDiff,
    type: TypeMasterDiff,
    item: ItemMasterDiff,
  ) => {
    console.log('-------------- Post Start --------------');

    // depthごとにアイテムを分類
    const depth0Items = item.post.filter((item) => item.depth === 0);
    const depth1Items = item.post.filter((item) => item.depth === 1);
    const depth2Items = item.post.filter((item) => item.depth === 2);

    for (const categoryData of category.post) {
      try {
        console.log(`【Category】Name : ${categoryData.categoryName}`);
        await postCategoryMaster(categoryData);
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.CATEGORY_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const typeData of type.post) {
      try {
        console.log(`【Type】 Name : ${typeData.typeName}`);
        await postTypeMaster(typeData);
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.TYPE_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    // depthごとに順番にリクエストを実行
    for (const itemData of depth0Items) {
      try {
        console.log(`【Item】Name : ${itemData.itemName}`);
        await postItemMaster(itemData);
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const itemData of depth1Items) {
      try {
        console.log(`【Item】Name : ${itemData.itemName}`);
        await postItemMaster(itemData);
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const itemData of depth2Items) {
      try {
        console.log(`【Item】Name : ${itemData.itemName}`);
        await postItemMaster(itemData);
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }
  };

  const doPutMaster = async (
    category: CategoryMasterDiff,
    type: TypeMasterDiff,
    item: ItemMasterDiff,
  ) => {
    console.log('-------------- Put Start --------------');
    await Promise.all(
      category.put.map(async (category) => {
        console.log(
          `【Category】ID : ${category.categoryId} Name : ${category.categoryName}`,
        );
        return await putCategoryMaster(category);
      }),
    ).catch((error) => {
      setImportErrorMsg(getApiErrorMessage(error, API_CODE.CATEGORY_MASTER));
      throw new Error('PUT_MASTER');
    });

    await Promise.all(
      type.put.map(async (type) => {
        console.log(`【Type】 ID : ${type.typeId} Name : ${type.typeName}`);
        return await putTypeMaster(type);
      }),
    ).catch((error) => {
      setImportErrorMsg(getApiErrorMessage(error, API_CODE.TYPE_MASTER));
      throw new Error('PUT_MASTER');
    });

    await Promise.all(
      item.put.map(async (item) => {
        console.log(`【Item】ID : ${item.itemId} Name : ${item.itemName}`);
        return await putItemMaster(item);
      }),
    ).catch((error) => {
      setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
      throw new Error('PUT_MASTER');
    });
  };

  const doDeleteMaster = async (
    category: CategoryMasterDiff,
    type: TypeMasterDiff,
    item: ItemMasterDiff,
  ) => {
    console.log('-------------- Delete Start --------------');

    // depthごとにアイテムを分類
    const depth0Items = item.delete.filter((item) => item.depth === 0);
    const depth1Items = item.delete.filter((item) => item.depth === 1);
    const depth2Items = item.delete.filter((item) => item.depth === 2);

    // 小項目はdepthが 2 → 1 → 0 の順に削除する
    for (const itemData of depth2Items) {
      try {
        console.log(`【Item】ID : ${itemData.itemId}`);
        await deleteItemMaster({ itemId: itemData.itemId });
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const itemData of depth1Items) {
      try {
        console.log(`【Item】ID : ${itemData.itemId}`);
        await deleteItemMaster({ itemId: itemData.itemId });
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const itemData of depth0Items) {
      try {
        console.log(`【Item】ID : ${itemData.itemId}`);
        await deleteItemMaster({ itemId: itemData.itemId });
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.ITEM_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const typeId of type.delete) {
      try {
        console.log(`【Type】 ID : ${typeId}`);
        await deleteTypeMaster({ typeId });
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.TYPE_MASTER));
        throw new Error('POST_MASTER');
      }
    }

    for (const categoryId of category.delete) {
      try {
        console.log(`【Category】ID : ${categoryId}`);
        await deleteCategoryMaster({ categoryId });
      } catch (error) {
        setImportErrorMsg(getApiErrorMessage(error, API_CODE.CATEGORY_MASTER));
        throw new Error('POST_MASTER');
      }
    }
  };

  return { onClickImportOk, onClickImportCancel };
};

export default useImport;
